import React, { createContext, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { setLocale, SUPPORTED_LOCALES, LOCALE_LABELS, LOCALE_FLAGS } from '../i18n/i18n';

// Simple typed translation function — t('dashboard.noSessions')
export type TFunc = (key: string, opts?: Record<string, unknown>) => string;

interface LanguageContextValue {
  locale: string;
  t: TFunc;
  setLocale: (locale: string) => void;
  supportedLocales: readonly string[];
  localeLabels: Record<string, string>;
  localeFlags: Record<string, string>;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n, t: i18nT } = useTranslation();

  const t: TFunc = useCallback(
    (key: string, opts?: Record<string, unknown>) => i18nT(key, opts) as string,
    [i18nT]
  );

  return (
    <LanguageContext.Provider value={{
      locale: i18n.language,
      t,
      setLocale,
      supportedLocales: SUPPORTED_LOCALES,
      localeLabels: LOCALE_LABELS,
      localeFlags: LOCALE_FLAGS,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}